// Advanced › Source control, simplified: "Versions". What is not saved as a
// version yet (ticked per screen / report / menu), saved versions with Shared /
// Only here, Compare, Go back to this, Get the latest and Share. Today's full
// source-control screen (branches, single files, conflicts) is unchanged under
// Advanced, for people who know git.

import { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { ChevronDown, ChevronRight, Download, Upload } from 'lucide-react'
import { useProjectStore } from '@/store/projectStore'
import { useProjectPermissions } from '@/auth/useProjectPermissions'
import { useAgentRefresh } from '@/hooks/useAgentRefresh'
import DiffViewer from '@/components/scm/DiffViewer'
import { SaveVersionPanel } from '@/components/workbench/SaveVersionDialog'
import {
  getScmCommit, getScmLog, getScmStatus, scmPull, scmPush, scmRestoreVersion,
} from '@/api/workbench'
import type { ScmCommit, ScmStatus } from '@/types'
import SourceControlTab from './SourceControlTab'

function errMsg(e: unknown, fallback: string): string {
  const d = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
  return typeof d === 'string' ? d : (e as Error)?.message || fallback
}

export default function VersionsPage() {
  const { currentProject } = useProjectStore()
  const perms = useProjectPermissions()
  const folder = currentProject?.application.folder_name || ''
  const [status, setStatus] = useState<ScmStatus | null>(null)
  const [versions, setVersions] = useState<ScmCommit[]>([])
  const [nonce, setNonce] = useState(0)
  const [busy, setBusy] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)
  const [compare, setCompare] = useState<{ sha: string; diff: string } | null>(null)
  const [advanced, setAdvanced] = useState(false)
  const agentNonce = useAgentRefresh(folder, false)

  const load = useCallback(async () => {
    if (!folder) return
    try {
      const [st, log] = await Promise.all([getScmStatus(folder), getScmLog(folder, 30, 0)])
      setStatus(st)
      setVersions(log.commits)
    } catch (e) {
      toast.error(errMsg(e, 'Could not read the versions'))
    }
  }, [folder])
  useEffect(() => { void load() }, [load, nonce, agentNonce])

  if (!folder) return null
  if (status && !status.is_repo) {
    // Not connected yet: the full screen explains how (RepositoryCard above it
    // creates the repository).
    return <SourceControlTab />
  }

  // Versions not on the shared copy yet: the newest `ahead` of the log.
  const unshared = status?.upstream ? Math.max(0, status.ahead || 0) : versions.length
  const run = async (label: string, fn: () => Promise<void>) => {
    setBusy(label)
    try { await fn() } finally { setBusy(null) }
  }
  const pull = () => run('pull', async () => {
    try {
      const r = await scmPull(folder)
      setNote(r.conflict
        ? `Two people changed the same thing — settle it under Advanced (${r.conflicted_files?.length || 0} file(s)).`
        : 'You have the latest.')
      setNonce((n) => n + 1)
    } catch (e) { toast.error(errMsg(e, 'Could not get the latest')) }
  })
  const push = () => run('push', async () => {
    try {
      const r = await scmPush(folder)
      setNote(r.rejected ? (r.reason || 'Get the latest first, then share.') : `Shared ${unshared} ${unshared === 1 ? 'version' : 'versions'}.`)
      setNonce((n) => n + 1)
    } catch (e) { toast.error(errMsg(e, 'Could not share')) }
  })
  const goBack = (v: ScmCommit) => run('restore', async () => {
    if (!window.confirm(
      `Go back to “${v.subject}”?\n\nThe files this version changed return to how they were in it. `
      + 'Your current changes to those files are replaced. Nothing is saved until you save a version.')) return
    try {
      const r = await scmRestoreVersion(folder, v.hash)
      setNote(`${r.files.length} file(s) are back to “${v.subject}”. Save a version to keep it.`)
      setNonce((n) => n + 1)
    } catch (e) { toast.error(errMsg(e, 'Could not go back')) }
  })
  const openCompare = async (sha: string) => {
    if (compare?.sha === sha) { setCompare(null); return }
    try {
      const d = await getScmCommit(folder, sha)
      setCompare({ sha, diff: d.diff })
    } catch (e) { toast.error(errMsg(e, 'Could not open that version')) }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }} data-tw-screen="versions">
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
        <span className="swb-muted" style={{ flex: '1 1 320px' }}>
          Save versions of your screens, share them, and go back to an earlier one.
        </span>
        {status?.branch && <span className="swb-chip swb-chip-neutral">Branch: {status.branch}</span>}
        {status?.upstream && (
          <button type="button" className="swb-btn swb-btn-secondary swb-btn-sm" disabled={!!busy || !perms.canEditObjects}
            onClick={() => { void pull() }} data-tw-action="versions-pull">
            <Download size={14} /> Get the latest
          </button>
        )}
        {status?.upstream && perms.canDeploy && (
          <button type="button" className="swb-btn swb-btn-primary swb-btn-sm" disabled={!!busy || unshared === 0}
            onClick={() => { void push() }} data-tw-action="versions-push">
            <Upload size={14} /> Share {unshared || ''} {unshared === 1 ? 'version' : 'versions'}
          </button>
        )}
      </div>
      {note && <div role="status" className="swb-notice swb-notice-info">{note}</div>}

      <section className="swb-card swb-card-pad" aria-label="Not saved yet" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <h2 style={{ margin: 0, fontSize: '1.05rem' }}>Not saved as a version yet</h2>
        {perms.canEditObjects
          ? <SaveVersionPanel project={folder} refreshKey={nonce + agentNonce} showVersions={false}
              onSaved={() => setNonce((n) => n + 1)} />
          : <span className="swb-muted">Only people who build the app can save versions.</span>}
      </section>

      <section className="swb-card swb-card-pad" aria-label="Saved versions" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <h2 style={{ margin: '0 0 4px', fontSize: '1.05rem' }}>Saved versions</h2>
        {versions.length === 0 && <span className="swb-muted">None yet.</span>}
        {versions.map((v, i) => {
          const shared = !!status?.upstream && i >= unshared
          return (
            <div key={v.hash} style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '8px 0', borderBottom: '1px solid var(--color-border)' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
                <span style={{ flex: '1 1 320px', display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontWeight: 600 }}>{v.subject}</span>
                  <span className="swb-muted" style={{ fontSize: 13 }}>{v.author} · {new Date(v.date).toLocaleString()}</span>
                </span>
                <span className={`swb-chip ${shared ? 'swb-chip-checked' : 'swb-chip-to_check'}`}>{shared ? 'Shared' : 'Only here'}</span>
                <code style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>{v.short_hash}</code>
                <button type="button" className="swb-link" onClick={() => { void openCompare(v.hash) }}>
                  {compare?.sha === v.hash ? 'Close' : 'Compare'}
                </button>
                {perms.canEditObjects && (
                  <button type="button" className="swb-link" disabled={!!busy} onClick={() => { void goBack(v) }}
                    data-tw-action={`versions-restore:${v.short_hash}`}>Go back to this</button>
                )}
              </div>
              {compare?.sha === v.hash && (
                <div style={{ maxHeight: 360, overflow: 'auto', border: '1px solid var(--color-border)', borderRadius: 6 }}>
                  <DiffViewer diff={compare.diff} />
                </div>
              )}
            </div>
          )
        })}
      </section>

      <section className="swb-card" aria-label="Advanced" style={{ display: 'flex', flexDirection: 'column' }}>
        <button type="button" onClick={() => setAdvanced((v) => !v)} aria-expanded={advanced}
          style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '14px 20px', border: 0, background: 'transparent',
            cursor: 'pointer', fontWeight: 600, fontSize: 15, textAlign: 'left', color: 'var(--color-text)', fontFamily: 'inherit',
          }}>
          {advanced ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          Advanced — branches, single files, conflicts
        </button>
        {advanced && (
          <div style={{ borderTop: '1px solid var(--color-border)', minHeight: 420, display: 'flex', flexDirection: 'column' }}>
            <SourceControlTab />
          </div>
        )}
      </section>
    </div>
  )
}
